import { useState } from "react";
import { Input } from "antd";
import { HttpStatusCode } from "axios";
import { ImCancelCircle } from "react-icons/im";
import style from "../style.module.scss";
import { CustomPopConfirm } from "../../../common/components/PopConfirm/CustomPopConfirm";
import { ReleaseOrderService } from "../store/_requests";
import {
  CANCEL_MESSAGE_COLOR,
  CANCEL_REASON_MAX_LENGTH,
} from "../core/_constant.releaseOrder";
import { ReleaseOrderItemData } from "../core/_model.releaseOrder";

const { TextArea } = Input;

type CancelExitNoteActionProps = {
  row: ReleaseOrderItemData;
  onCancelled: () => void;
};

/**
 * PATCH /exit_notes/{id}/cancel. The backend keeps the note for traceability and
 * releases its removals, but requires a reason (Assert\NotBlank), hence the field
 * inside the confirmation.
 */
export const CancelExitNoteAction = ({
  row,
  onCancelled,
}: CancelExitNoteActionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleClose = () => {
    setIsOpen(false);
    setReason("");
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();

    if (!trimmedReason || row?.id == null) {
      return;
    }

    try {
      setIsSubmitting(true);

      const res = await ReleaseOrderService.cancelReleaseOrder(
        row.id,
        trimmedReason
      );

      if (res?.status === HttpStatusCode.Ok) {
        handleClose();
        onCancelled();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomPopConfirm
      isOpen={isOpen}
      title={`Annuler le bon de sortie ${row?.reference ?? ""} ?`}
      messageColor={CANCEL_MESSAGE_COLOR}
      okType="danger"
      okText="Annuler le bon"
      cancelText="Retour"
      placement="bottomRight"
      okButtonProps={{
        loading: isSubmitting,
        disabled: !reason.trim(),
      }}
      onClose={handleClose}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      onConfirm={handleConfirm}
      description={
        <div className={style.cancelMessage}>
          <p className={style.cancelMessage__text}>
            Le bon de sortie est conservé pour la traçabilité et ses enlèvements
            redeviennent disponibles pour un nouveau bon.
          </p>

          <label className={style.cancelMessage__label}>
            Motif d'annulation *
          </label>

          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Précisez le motif de l'annulation"
            maxLength={CANCEL_REASON_MAX_LENGTH}
            autoSize={{ minRows: 2, maxRows: 3 }}
            status={reason.trim() ? undefined : "warning"}
          />
        </div>
      }
    >
      <div
        className="cursor-pointer d-flex align-items-center"
        onClick={() => setIsOpen(true)}
        title="Annuler le bon de sortie"
      >
        <ImCancelCircle color="#db4145" size={15.5} className="mt-[1px]"/>
      </div>
    </CustomPopConfirm>
  );
};
